import { FC, useEffect, useState } from "react";
import { useCountriesApi } from "@/hooks/useCountriesApi";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";

interface BrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (brandData: {
    name: string;
    countryId?: number;
    groupingDistanceMeters?: number;
  }) => void;
}

const BrandModal: FC<BrandModalProps> = ({ isOpen, onClose, onSave }) => {
  const [brandName, setBrandName] = useState("");
  const [countryId, setCountryId] = useState<number | undefined>(undefined);
  const [groupingDistanceMeters, setGroupingDistanceMeters] =
    useState<number>(50);
  const [error, setError] = useState("");
  const {
    countries,
    fetchCountries,
    loading: countriesLoading,
  } = useCountriesApi();

  useEffect(() => {
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // fetch countries when opening
      fetchCountries();
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, fetchCountries]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setBrandName("");
      setCountryId(undefined);
      setGroupingDistanceMeters(50);
      setError("");
    }
  }, [isOpen]);

  const validateBrandName = (name: string): boolean => {
    // Check if name is empty
    if (name.trim().length === 0) {
      setError("El nombre no puede estar vacío");
      return false;
    }
    // Check if name is too short (less than 2 characters)
    if (name.trim().length < 2) {
      setError("El nombre debe tener al menos 2 caracteres");
      return false;
    }
    // Only allow letters, numbers, spaces, hyphens, and &
    const validChars = /^[a-zA-Z0-9\s\-&]+$/;
    if (!validChars.test(name)) {
      setError(
        "El nombre solo puede contener letras, números, espacios, guiones y &. No se permiten otros caracteres especiales.",
      );
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateBrandName(brandName)) {
      onSave({
        name: brandName.trim(),
        countryId: countryId,
        groupingDistanceMeters: groupingDistanceMeters,
      });
      setBrandName("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 text-center">
        <div
          className="fixed inset-0 bg-black/30 transition-opacity duration-300"
          onClick={onClose}
          style={{ opacity: isOpen ? 1 : 0 }}
        ></div>

        <div
          className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
          style={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? "scale(1)" : "scale(0.95)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Crear nueva marca
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la marca
              </label>
              <input
                type="text"
                className={`bg-gray-50 border ${error ? "border-red-500" : "border-gray-300"} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="Ingresa nombre de la marca"
                value={brandName}
                onChange={(e) => {
                  setBrandName(e.target.value);
                  if (error) validateBrandName(e.target.value);
                }}
                required
              />
              {error && (
                <div className="mt-2">
                  <p className="text-sm text-red-600" role="alert">
                    {error}
                  </p>
                </div>
              )}
            </div>

            <div className="">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                País
              </label>
              <Select
                value={countryId !== undefined ? String(countryId) : undefined}
                onValueChange={(val) =>
                  setCountryId(val === "none" ? undefined : Number(val))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un país" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="inline-flex items-center gap-2">
                        {c.flag_url ? (
                          <img
                            src={c.flag_url}
                            alt={c.name}
                            className="h-3 w-3 rounded-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                        <span>{c.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {countriesLoading && (
                <p className="text-xs text-gray-400 mt-1">Cargando países...</p>
              )}
            </div>

            <div className="space-y-2 ">
              <label className="block text-sm font-medium text-gray-700">
                Distancia de agrupación de órdenes
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                <div className="flex-1">
                  <Select
                    value={groupingDistanceMeters.toString()}
                    onValueChange={(val) =>
                      setGroupingDistanceMeters(Number(val))
                    }
                    disabled={false}
                  >
                    <SelectTrigger className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg disabled:bg-gray-50 disabled:text-gray-600">
                      <SelectValue placeholder="Seleccionar distancia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50 mts</SelectItem>
                      <SelectItem value="100">100 mts</SelectItem>
                      <SelectItem value="200">200 mts</SelectItem>
                      <SelectItem value="300">300 mts</SelectItem>
                      <SelectItem value="500">500 mts</SelectItem>
                      <SelectItem value="1000">1 km</SelectItem>
                      <SelectItem value="2000">2 km</SelectItem>
                      <SelectItem value="5000">5 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BrandModal;
